import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { useRequestPasswordResetMutation } from "../../store/api/authApi";

const PasswordResetRequestPage: React.FC = () => {
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [message, setMessage] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [requestReset, { isLoading }] = useRequestPasswordResetMutation();

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setMessage(null);
		setError(null);

		if (!email.trim()) {
			setError("Please enter your email address.");
			return;
		}

		try {
			// Normalize email to lowercase before sending
			const normalizedEmail = email.trim().toLowerCase();
			const result = await requestReset({ email: normalizedEmail }).unwrap();
			setMessage(
				result.message || "If this email exists, a reset link has been sent.",
			);
		} catch (err: any) {
			console.error("Password reset request failed:", err);
			if (err?.data?.error) {
				setError(err.data.error);
			} else if (err?.data?.message) {
				setError(err.data.message);
			} else {
				setError("Unable to process request. Please try again.");
			}
		}
	};

	return (
		<div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background p-4">
			<div className="absolute inset-0 opacity-5">
				<div
					className="absolute inset-0"
					style={{
						backgroundImage:
							"url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
					}}
				/>
			</div>

			<div className="relative w-full max-w-md">
				<div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8">
					<div className="absolute -right-16 -top-16 h-32 w-32 rounded-full bg-primary/5" />
					<div className="absolute -bottom-12 -left-12 h-24 w-24 rounded-full bg-accent/5" />

					<div className="relative z-10 space-y-6">
						<div className="text-center">
							<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
								<Icon name="KeyRound" size={20} />
							</div>
							<h1 className="text-2xl font-semibold text-foreground">
								Forgot your password?
							</h1>
							<p className="mt-2 text-sm text-muted-foreground">
								Enter the email associated with your account and we&apos;ll send
								you instructions to reset your password.
							</p>
						</div>

						<form onSubmit={handleSubmit} className="space-y-4">
							<div>
								<label className="mb-2 block text-sm font-medium text-foreground">
									Email Address
								</label>
								<Input
									type="email"
									name="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="Enter your email"
									required
								/>
							</div>

							{error && (
								<div className="rounded-lg bg-destructive/10 p-3 text-center text-sm text-destructive">
									{error}
								</div>
							)}

							{message && !error && (
								<div className="rounded-lg bg-success/10 p-3 text-center text-sm text-success">
									{message}
								</div>
							)}

							<Button type="submit" className="w-full" disabled={isLoading}>
								{isLoading ? (
									<>
										<Icon
											name="Loader2"
											size={16}
											className="mr-2 animate-spin"
										/>
										Sending reset link...
									</>
								) : (
									<>
										<Icon name="Mail" size={16} className="mr-2" />
										Send Reset Link
									</>
								)}
							</Button>
						</form>

						<div className="text-center">
							<button
								type="button"
								className="text-sm text-muted-foreground transition-colors hover:text-primary"
								onClick={() => navigate("/login")}
							>
								← Back to Login
							</button>
						</div>
					</div>
				</div>

				<div className="mt-6 text-center">
					<p className="text-xs text-muted-foreground">
						If you don&apos;t receive an email in a few minutes, please check
						your spam folder.
					</p>
				</div>
			</div>
		</div>
	);
};

export default PasswordResetRequestPage;
